import React, { useState, useEffect } from 'react';
import { ShieldAlert, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import * as API from '../lib/api';
import { DEMO_VIOLATIONS } from '../types/quant-audit';
import QuantAuditPanel from '../components/audit/QuantAuditPanel';

export default function Audit() {
  const { currentAnalysis } = useApp();
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usingDemo, setUsingDemo] = useState(false);

  useEffect(() => {
    if (!currentAnalysis?.id) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await API.getAuditViolations(currentAnalysis.id);
        if (!cancelled) {
          setViolations(data?.violations || data || []);
          setUsingDemo(false);
        }
      } catch {
        if (!cancelled) {
          setViolations(DEMO_VIOLATIONS);
          setUsingDemo(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [currentAnalysis?.id]);

  const handleLoadDemo = () => {
    setViolations(DEMO_VIOLATIONS);
    setUsingDemo(true);
    toast.success('Demo audit data loaded');
  };

  return (
    <div className="space-y-6 animate-fade-in text-[#d8dae0]">
      <div className="relative overflow-hidden rounded-xl border border-[#ffffff0a] bg-[#090b10] p-6 shadow-2xl">
        <div className="absolute top-0 left-0 h-[2px] w-1/3 bg-gradient-to-r from-transparent via-[#ff3b30] to-transparent opacity-70" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(255,59,48,0.04),_transparent_40%)]" />
        <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="mb-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#ff3b30]">
              <ShieldAlert className="w-3.5 h-3.5" />
              Quant Audit Panel
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-white mb-1">
              Rule-by-Rule Compliance
            </h1>
            <p className="text-xs text-[#8b93a7] font-mono">
              {currentAnalysis?.id
                ? `Auditing analysis: ${currentAnalysis.id.slice(0, 8)}...`
                : 'No active analysis. Load demo data to preview the audit panel.'}
              {usingDemo && ' (Demo data)'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!currentAnalysis?.id && (
              <button
                onClick={handleLoadDemo}
                className="flex items-center gap-1.5 px-4 py-2 rounded text-[11px] font-mono font-medium bg-white/5 text-white border border-white/10 hover:bg-white/10 transition-all"
              >
                <Download className="w-3 h-3" /> Load Demo Data
              </button>
            )}
          </div>
        </div>
      </div>

      <QuantAuditPanel violations={violations} loading={loading} />
    </div>
  );
}
